/**
 * FieldGroupContext
 *
 * Purpose: Provides a context stack for validation-aware containers (Tabs, Accordion)
 *          so parent containers can detect which child fields have errors
 * Architecture: Context stack pattern — each FieldGroupProvider pushes itself onto
 *               the stack; nested Fields register with ALL ancestor groups via useFieldGroups()
 * Dependencies: React (createContext, useContext, useRef, forwardRef, useImperativeHandle),
 *               ./types (FieldGroupContextValue, FieldGroupHandle),
 *               ./TemplateFormContext (useTemplateFormContext)
 * Author: Development Team
 * Created: 2026-02-15
 */

import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import type { FieldGroupContextValue, FieldGroupHandle } from './types';
import { useTemplateFormContext } from './TemplateFormContext';

/**
 * Stack of FieldGroup contexts for nested registration.
 *
 * Purpose: Maintains an ordered list of ancestor FieldGroup contexts
 * Logic: Initialized to empty array; each FieldGroupProvider appends itself
 */
const FieldGroupStackContext = createContext<FieldGroupContextValue[]>([]);

/**
 * useFieldGroups — returns all ancestor FieldGroup contexts
 *
 * Purpose: Allows Field components to register with every ancestor FieldGroup
 * Logic: Reads the FieldGroupStackContext which contains all ancestor groups
 * Input: None
 * Output: Array of FieldGroupContextValue from outermost to innermost ancestor
 */
export const useFieldGroups = (): FieldGroupContextValue[] => {
  return useContext(FieldGroupStackContext);
};

/**
 * FieldGroupProvider — wraps content (Tab or Panel) to track child field IDs
 *
 * Purpose: Tracks which field IDs are registered within its subtree and exposes
 *          a FieldGroupHandle via ref for querying validation error state
 * Logic: Maintains a Set of registered fieldDefIds; checks the form errors map
 *        to determine if any registered fields have errors
 * Input: children (React nodes to wrap), ref (FieldGroupHandle for parent access)
 * Output: Renders children wrapped in a FieldGroupStackContext.Provider
 */
export const FieldGroupProvider = forwardRef<FieldGroupHandle, { children: React.ReactNode }>(
  function FieldGroupProvider({ children }, ref) {
    const fieldIds = useRef<Set<string>>(new Set());
    const parentGroups = useContext(FieldGroupStackContext);
    const { errors } = useTemplateFormContext();

    const groupValue: FieldGroupContextValue = {
      register: useCallback((id: string) => {
        fieldIds.current.add(id);
      }, []),
      unregister: useCallback((id: string) => {
        fieldIds.current.delete(id);
      }, []),
    };

    useImperativeHandle(
      ref,
      () => ({
        hasErrors: () => {
          for (const id of fieldIds.current) {
            if (errors[id]) return true;
          }
          return false;
        },
        getErrorFieldIds: () => {
          return Array.from(fieldIds.current).filter((id) => !!errors[id]);
        },
        getFieldIds: () => Array.from(fieldIds.current),
      }),
      [errors]
    );

    // Push this group onto the stack so nested Fields can register with all ancestors
    const stack = [...parentGroups, groupValue];

    return (
      <FieldGroupStackContext.Provider value={stack}>{children}</FieldGroupStackContext.Provider>
    );
  }
);
